<template>
  <Teleport to="head">
    <component
      v-if="SchemaOrgCollectionPageJsonLd"
      :is="'script'"
      type="application/ld+json"
      :key="SchemaOrgCollectionPageJsonLd"
      v-text="SchemaOrgCollectionPageJsonLd"
    />
  </Teleport>
  <div class="u-position-relative">
    <!-- Preview bar -->
    <template v-if="isPreview">
      <PreviewBar v-if="isPreview" @clickExit="clickExitPreview()" />
    </template>
    <!-- Category result -->
    <template v-if="categoryResult">
      <!-- Loader -->
      <div v-if="productFilterStore.isLoading || isFallbackLoading" class="loader-wrapper">
        <div class="loader" />
      </div>
      <!-- No Products Fallback -->
      <div
        v-if="
          !productFilterStore.isLoading &&
          !isFallbackLoading &&
          noProductsFound &&
          productFilterStore.selectedFiltersCount > 0 &&
          !hasFallbackProducts
        "
        class="product-filter__noproductstext u-text-center"
        role="alert"
      >
        <p>{{ $t('noHitsFilter') }}</p>
        <button
          class="product-filter__noproductsbutton btn btn--primary btn--small"
          @click="clearAllFilters"
        >
          {{ $t('clearAll') }}
        </button>
      </div>
      <!-- Parent category fallback -->
      <template
        v-if="
          !productFilterStore.isLoading &&
          !isFallbackLoading &&
          noProductsFound &&
          fallbackResult &&
          fallbackResult.primaryList &&
          fallbackResult.primaryList.productGroups.length > 0
        "
      >
        <div class="category-page__fallback-cta">
          <h1 class="layout-category__heading">{{ heading }}</h1>
          <div v-if="props.seoInformation" class="sr-only">{{ props.seoInformation }}</div>
          <div class="layout-category__seo-information" aria-live="polite">
            Denne kategorien har ingen produkter for øyeblikket. Her er et utvalg produkter vi tror du vil like
          </div>
        </div>
        <products
          v-bind="fallbackResult"
          :subcategories="subcategories"
          :heading="heading"
          :hide-heading="true"
          :seoInformation="props.seoInformation"
          :searchpage="false"
          :is-user-authenticated="isUserAuthenticated"
          :hide-favorites="hideFavorites"
          :is-fallback="true"
          :there-are-more-pages="false"
          :use-product-gtm-tracking="false"
          :user-id="userId"
          :category-blocks="[]"
          :store="store"
          @upsertFacet="upsertFacet($event)"
          @useFilter="ApplyFilter()"
          @updateSort="updateSort($event)"
          @toggleFavorite="clickFavorite($event)"
        />
      </template>
      <!-- Products -->
      <products
        v-if="
          categoryResult &&
          categoryResult.primaryList &&
          categoryResult.primaryList.productGroups &&
          categoryResult.primaryList.productGroups.length > 0
        "
        v-bind="categoryResult"
        :subcategories="subcategories"
        :heading="heading"
        :hide-heading="hideHeading"
        :seoInformation="props.seoInformation"
        :searchpage="false"
        :is-user-authenticated="isUserAuthenticated"
        :hide-favorites="hideFavorites"
        :there-are-more-pages="thereAreMorePages"
        :use-product-gtm-tracking="true"
        :user-id="userId"
        :category-blocks="categoryBlocks"
        :store="store"
        @upsertFacet="upsertFacet($event)"
        @useFilter="ApplyFilter()"
        @nextPage="nextPage()"
        @toggleFavorite="clickFavorite($event)"
        @updateSort="updateSort($event)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { routerPush } from '../../../../helpers/routing';
import PreviewBar from '../../components/previewBar/previewBar.vue';
import SearchService, { CategoryResult, Facet } from '../../services/search.service';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { useProductFilterStore } from '../../stores/productFilterStore';
import { Log } from '../../utils/API/Logging/LoggingAPI';
import { debounce } from '../../utils/debounce';
import { getLabel } from '../../utils/search.utils';
import {
  getCategoryImpressionsEvent,
  getFilterImpressionsEvent,
  pushDataToGtm,
} from '../../utils/tracking';
import { getSelectedValuesFromFacet } from '../searchPage/searchPage.utils';
import Products from './../searchPage/products.vue';

const FACET_PREFIX = 'filter.';
const FACET_DELIMITER = '|';

enum TrackingEvents {
  CategoryPage = 'categoryPage',
  FiltePage = 'FilterPage',
}

export type CategoryBlock = {
  imageUrl: string;
  imageAltText: string;
  heading: string;
  subHeading: string;
  text: string;
  link: string;
  rowIndex: number;
  leftOrRight: string;
  layout: string;
};

export type SubCategories = { name: string; url: string; isSelected: boolean }[] | null;
//Dataset means all props are strings for now
const props = defineProps<{
  userId: string;
  store: string;
  heading: string;
  hideHeading: string | null;
  category: string;
  subcategoriesIncoming?: string | null;
  favoriteFormHidden: string;
  userAuthenticated: string;
  pageSize: string;
  pageId: string;
  tracking: string;
  contentlinkId: number | undefined;
  seoInformation: string;
  categoryBlocks: string; // JSON string
  parentPageId?: string | null;
}>();

const favoritesStore = useFavoritesStore();
const productFilterStore = useProductFilterStore();

const page = ref(0);
const abortController = ref<AbortController | null>(null);
const searchFacets = ref({});
const categoryResult = ref(null as CategoryResult | null);
const fallbackResult = ref(null as CategoryResult | null);
const isFallbackLoading = ref(false);
const sort = ref(null as string | null);
const preview = ref(undefined as string | undefined);
const categoryBlocks = ref(Array<CategoryBlock>());

// Get routes
const route = useRoute();
const router = useRouter();

// Used to insert https://schema.org/CollectionPage into head of the page
const SchemaOrgCollectionPageJsonLd = computed(() => {
  const groups = categoryResult.value?.primaryList?.productGroups;
  if (!groups || groups.length === 0) return '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: props.heading,
    description: props.seoInformation,
    url: window.location.href,
    mainEntity: groups.map((group) => ({
      '@type': 'ItemList',
      name: 'Produktliste',
      product: group.products.map((product) => ({
        '@type': 'Product',
        name: product.title,
        image: product.imageInfo?.thumbnail,
        url: product.link,
        brand: product.brand,
        offers: {
          '@type': 'Offer',
          priceCurrency: 'NOK',
          price: product.sellingPrice?.max,
          availability: product.inStock
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        },
      })),
    })),
  };

  return JSON.stringify(schema, null, 2);
});

const pageSizeNumber = computed((): number => {
  return props.pageSize ? Number(props.pageSize) : 12;
});

const thereAreMorePages = computed((): boolean => {
  if (!categoryResult.value || !categoryResult.value.primaryList) {
    return false;
  }

  const limit = (page.value + 1) * pageSizeNumber.value;

  return categoryResult.value.primaryList.totalHits > limit;
});

const noProductsFound = computed(
  () =>
    categoryResult.value &&
    categoryResult.value.primaryList &&
    categoryResult.value.primaryList.productGroups &&
    categoryResult.value.primaryList.productGroups.length === 0
);

const hasFallbackProducts = computed(
  () => (fallbackResult.value?.primaryList?.productGroups?.length ?? 0) > 0
);

const subcategories = computed(() => {
  if (!props.subcategoriesIncoming) return null;

  try {
    const subcategories = JSON.parse(props.subcategoriesIncoming);
    // const doubledSubcats = [
    //   ...subcategories.map((subcat) => ({ ...subcat, isSelected: false })),
    //   ...subcategories,
    // ];
    // doubledSubcats[0].name = "Vis alle";
    // return doubledSubcats;
    subcategories[0].name = 'Vis alle';
    return subcategories;
  } catch {
    return [];
  }
});

//We're getting booleans as string. Convert to bools
const hideFavorites = computed((): boolean => {
  return props.favoriteFormHidden === 'True';
});
const isUserAuthenticated = computed((): boolean => {
  return props.userAuthenticated === 'True';
});
const isPreview = computed((): boolean => {
  return preview.value === 'true';
});
const hideHeading = computed((): boolean => {
  return props.hideHeading === 'True';
});

onMounted(() => {
  if (isUserAuthenticated.value) {
    favoritesStore.getFavorites();
  }

  if (props.categoryBlocks) {
    categoryBlocks.value = JSON.parse(props.categoryBlocks) as Array<CategoryBlock>;
  }
});

watch(
  [() => route.fullPath, () => route.hash],
  ([toPath, toHash], [fromPath, fromHash]) => {
    /**
     * Just hash change: Don't search
     */
    if (
      toHash &&
      fromHash &&
      fromPath &&
      toHash !== fromHash &&
      toPath.replace(toHash, '') === fromPath.replace(fromHash, '')
    ) {
      return;
    }
    /**
     * Initial navigation: Trigger search
     */
    if (!fromPath) {
      search();
      return;
    }
    /**
     * Route has changed: Trigger search
     */
    if (toPath !== fromPath) {
      search();
    }
  },
  { immediate: true }
);

watch(categoryResult, () => {
  const result = categoryResult.value;

  if (!result?.primaryList) return;

  if (result.primaryList.productGroups.length === 0 && props.parentPageId) {
    // Category is empty — fetch parent as fallback, passing any active facets/sort from the URL
    fetchParentCategoryFallback(searchFacets.value, sort.value);
    return;
  }

  // GTM tracking
  if (props.tracking === TrackingEvents.CategoryPage) {
    const tracking = getCategoryImpressionsEvent({
      userId: props.userId,
      store: props.store,
      productGroups: result.primaryList.productGroups,
    });
    pushDataToGtm(tracking);
  } else if (props.tracking === TrackingEvents.FiltePage) {
    const tracking = getFilterImpressionsEvent({
      store: props.store,
      listName: '',
      productGroups: result.primaryList.productGroups,
    });
    pushDataToGtm(tracking);
  }

  // Update filters in store
  productFilterStore.filters = result.primaryList.facets;

  // Update total hits in store
  productFilterStore.totalHits = result.primaryList.totalHits;

  // Optionally also hydrate selectedFilters
  productFilterStore.selectedFilters = result.primaryList.facets.flatMap((facet) =>
    getSelectedValuesFromFacet(facet).map((facetValue) => ({
      facetId: facet.id,
      facetValueId: facetValue.id,
      label: getLabel(facetValue),
    }))
  );
});

watch(fallbackResult, () => {
  const result = fallbackResult.value;

  if (!result?.primaryList) return;

  // Populate the store with the parent category's facets so filters/sort work correctly
  productFilterStore.filters = result.primaryList.facets;
  productFilterStore.totalHits = result.primaryList.totalHits;
  productFilterStore.selectedFilters = result.primaryList.facets.flatMap((facet) =>
    getSelectedValuesFromFacet(facet).map((facetValue) => ({
      facetId: facet.id,
      facetValueId: facetValue.id,
      label: getLabel(facetValue),
    }))
  );
});

function clickFavorite(productKey: string) {
  favoritesStore.toggleFavorite(productKey);
}

/**
 * Search
 */
function search() {
  searchFacets.value = getSearchFacetsFromRoute(route);
  sort.value = getSortFromRoute(route);
  page.value = getPageFromRoute(route);
  preview.value = getPreviewFromRoute(route);
  fallbackResult.value = null;
  isFallbackLoading.value = false;

  abortController.value?.abort();
  abortController.value = new AbortController();
  productFilterStore.isLoading = true;

  SearchService.category(
    abortController.value,
    props.pageId,
    searchFacets.value,
    sort.value,
    pageSizeNumber.value,
    page.value,
    preview.value,
    props.contentlinkId
  )
    .then((result) => {
      categoryResult.value = result.data;
    })
    .catch((error) => {
      categoryResult.value = null;

      // Only log errors that are not aborts
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        Log({
          LogLevel: 'Warning',
          Message: `[categoryPage] Failed to fetch category products: ${error instanceof Error ? error.message : String(error)}`,
        });
      }
    })
    .finally(() => {
      productFilterStore.isLoading = false;
    });
}

/**
 * Fetch products from the parent category as a fallback when the current category is empty
 */
function fetchParentCategoryFallback(facets = {}, currentSort: string | null = null) {
  if (!props.parentPageId) return;

  isFallbackLoading.value = true;
  const controller = new AbortController();
  SearchService.category(
    controller,
    props.parentPageId,
    facets,
    currentSort,
    pageSizeNumber.value,
    0,
    undefined,
    undefined
  )
    .then((result) => {
      fallbackResult.value = result.data;
    })
    .catch((error) => {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        Log({
          LogLevel: 'Error',
          Message: `[categoryPage] Failed to fetch parent category fallback: ${error instanceof Error ? error.message : String(error)}`,
        });
      }
    })
    .finally(() => {
      isFallbackLoading.value = false;
    });
}

/**
 * Clear all filters
 */
function clearAllFilters() {
  if (!categoryResult.value?.primaryList?.facets) return;

  categoryResult.value.primaryList.facets.forEach((facet) => {
    upsertFacet({
      facet,
      selectedValues: [],
      useFilter: false, // Avoid triggering ApplyFilter repeatedly
    });
  });

  ApplyFilter(); // Trigger once after all facets are cleared
}

/**
 * Upsert facet
 */
function upsertFacet({
  facet,
  selectedValues,
  useFilter = false,
}: {
  facet: Facet;
  selectedValues: Array<string>;
  useFilter: boolean;
}) {
  searchFacets.value[facet.id] = selectedValues;
  if (useFilter) {
    ApplyFilter();
  }
}
/**
 * Update sort
 */
function updateSort(newSort: string): void {
  sort.value = newSort;
}
/**
 * Use filter
 */
const ApplyFilter = debounce(() => {
  const currentRouterQuery = route.query;
  const newRouterQuery = { ...currentRouterQuery };

  Object.keys(searchFacets.value).forEach((facetId) => {
    const selectedValues = searchFacets.value[facetId];
    newRouterQuery[`${FACET_PREFIX}${facetId}`] = selectedValues.join(FACET_DELIMITER);

    if (selectedValues.length === 0) {
      delete newRouterQuery[`${FACET_PREFIX}${facetId}`];
    }
  });

  delete newRouterQuery['page'];

  if (sort.value) {
    newRouterQuery.sort = sort.value;
  } else {
    delete newRouterQuery.sort;
  }

  routerPush(
    {
      name: route.name,
      query: newRouterQuery,
    },
    router
  );
}, 300); // delay in ms

/**
 * Next page
 */
function nextPage() {
  const page = getPageFromRoute(route);

  routerPush(
    {
      name: route.name,
      query: {
        ...route.query,
        page: page + 1,
      },
    },
    router
  );
}
/**
 * Click exit preview
 */
function clickExitPreview() {
  routerPush(
    {
      name: route.name,
      query: {
        q: route.query.q,
      },
    },
    router
  );
}

function getSearchFacetsFromRoute($route) {
  if (!$route.query) {
    return [];
  }

  const searchFacets = {};

  Object.keys($route.query)
    .filter((key) => {
      // For all query params beginning with prefix
      return key.indexOf(FACET_PREFIX) === 0 && key.length > FACET_PREFIX.length;
    })
    .forEach((key) => {
      // Remove prefix and split on delimiter
      searchFacets[key.split(FACET_PREFIX)[1]] = $route.query[key].split(FACET_DELIMITER);
    });

  return searchFacets;
}

function getSortFromRoute($route): string | null {
  return $route.query.sort || null;
}

function getPageFromRoute($route): number {
  if (typeof $route.query.page !== 'undefined') {
    return Number($route.query.page);
  }

  return 0;
}

function getPreviewFromRoute($route): string {
  return $route.query.PREVIEW;
}
</script>
